import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PHOTOS } from "./assets";

const PROGRAMS = [
  {
    title: "Speed Development",
    tag: "Flagship",
    desc: "Sprint mechanics, acceleration and top-end speed. Get measurably faster on the field.",
    img: PHOTOS.speed,
    pos: "object-[center_30%]",
    span: "lg:col-span-8",
  },
  {
    title: "Strength & Power",
    tag: "Foundation",
    desc: "Force production and explosive power built through structured progressive loading.",
    img: PHOTOS.strength,
    pos: "object-[center_35%]",
    span: "lg:col-span-4",
  },
  {
    title: "Combine Prep",
    tag: "Testing",
    desc: "40-yard dash, shuttle, vertical and broad jump — dialed in for your showcase.",
    img: PHOTOS.combine,
    pos: "object-[center_20%]",
    span: "lg:col-span-4",
  },
  {
    title: "Youth Athletic Development",
    tag: "Long-term",
    desc: "Age-appropriate coordination, speed and strength that builds durable athletes.",
    img: PHOTOS.youth,
    pos: "object-[center_35%]",
    span: "lg:col-span-8",
  },
];

export default function Programs() {
  const scrollTo = () =>
    document.querySelector("#assessment")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="programs"
      data-testid="programs-section"
      className="py-24 md:py-36 bg-black border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="font-mono-accent text-xs tracking-[0.35em] uppercase text-white/40 mb-6">
              What We Build
            </p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
              Training Focus
            </h2>
          </div>
          <p className="max-w-sm text-white/55">
            Every program runs on the ORIGIN™ System — assessed, individualized,
            and tracked from day one. Mobile, off-site training across Northeast Ohio.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {PROGRAMS.map((p, i) => (
            <motion.button
              key={p.title}
              onClick={scrollTo}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`program-card-${i}`}
              className={`group relative overflow-hidden text-left border border-white/10 hover:border-white/30 transition-colors duration-300 min-h-[320px] md:min-h-[380px] ${p.span}`}
            >
              <img
                src={p.img}
                alt={p.title}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover grayscale opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700 ${p.pos}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-between p-8">
                <div className="flex items-center justify-between">
                  <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/70 border border-white/30 px-3 py-1">
                    {p.tag}
                  </span>
                  <ArrowUpRight
                    size={26}
                    className="text-white/60 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
                  />
                </div>
                <div>
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-3xl md:text-4xl mb-2">
                    {p.title}
                  </h3>
                  <p className="text-white/60 max-w-md text-sm md:text-base">
                    {p.desc}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}
